import { Fragment } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import { wv } from '@/utils/screenUtil';

import { PrimaryIconButton } from './PrimaryIconButton';
import { SafeArea } from './SafeArea';
import styles from './BackgroundWrapper.module.css';

/**
 * Перенос `lib/src/presentation/widgets/background_wrapper.dart`.
 *
 * Раскладка экрана: шапка, подвал и кнопка «назад».
 *
 * Сам фон рисуется один раз под навигатором — см. `AppBackground`.
 */

/** PrimaryIconButton(iconSize: 30, size: 80) у кнопки «назад». */
const BACK_BUTTON_ICON_SIZE = 30;
const BACK_BUTTON_SIZE = 80;

/** Список виджетов из Dart: ключи там позиционные, здесь тоже. */
function keyed(nodes: ReactNode[]): ReactElement[] {
  return nodes.map((node, index) => <Fragment key={index}>{node}</Fragment>);
}

interface ActionsBarProps {
  actions: ReactNode[];
  stackedActions: ReactNode[];
  /** Готовая CSS-длина: у подвала масштабированная, у шапки — нет. */
  spacing: string;
  scrollable: boolean;
}

/** Stack(Row(spacing), ...stackedActions) из подвала и шапки. */
function ActionsBar(props: ActionsBarProps): ReactElement {
  const { actions, stackedActions, spacing, scrollable } = props;
  const rowClass = [styles.row, scrollable ? styles.scrollable : null];
  return (
    <div className={styles.bar}>
      <div
        className={rowClass.filter(Boolean).join(' ')}
        style={{ '--actions-spacing': spacing } as CSSProperties}
      >
        {keyed(actions)}
      </div>
      {keyed(stackedActions)}
    </div>
  );
}

/**
 * `context.pop()`.
 *
 * Роутер не нужен: история у вкладки одна, и `history.back()` для
 * react-router — тот же самый переход назад.
 */
function BackButton(): ReactElement {
  return (
    <SafeArea className={styles.backButton}>
      <div className={styles.backButtonPadding}>
        <PrimaryIconButton
          icon="arrowLeft"
          iconSize={BACK_BUTTON_ICON_SIZE}
          size={BACK_BUTTON_SIZE}
          onPressed={() => window.history.back()}
        />
      </div>
    </SafeArea>
  );
}

export interface BackgroundWrapperProps {
  children: ReactNode;
  footerActions?: ReactNode[];
  headerActions?: ReactNode[];
  footerStackedActions?: ReactNode[];
  headerStackedActions?: ReactNode[];
  footerActionsSpacing?: number;
  headerActionsSpacing?: number;
  footerScrollable?: boolean;
  headerScrollable?: boolean;
  automaticallyImplyLeading?: boolean;
  /**
   * `ModalRoute.of(context)?.impliesAppBarDismissal` — во Flutter это знал
   * сам маршрут. В вебе страница сообщает об этом сама.
   */
  canPop?: boolean;
}

export function BackgroundWrapper(props: BackgroundWrapperProps): ReactElement {
  const {
    children, footerActions = [], headerActions = [], footerStackedActions = [],
    headerStackedActions = [], footerActionsSpacing = 20, headerActionsSpacing = 20,
    footerScrollable = false, headerScrollable = false,
    automaticallyImplyLeading = false, canPop = false,
  } = props;
  const showBackButton = automaticallyImplyLeading ? canPop : false;
  const hasFooter = footerActions.length > 0 || footerStackedActions.length > 0;
  const hasHeader = headerActions.length > 0 || headerStackedActions.length > 0;

  return (
    <div className={styles.root}>
      {showBackButton && <BackButton />}
      <div className={styles.content}>
        <div className={styles.child}>{children}</div>
        {hasFooter && (
          <div className={styles.footer}>
            <SafeArea top={false}>
              <ActionsBar
                actions={footerActions}
                stackedActions={footerStackedActions}
                spacing={wv(footerActionsSpacing)}
                scrollable={footerScrollable}
              />
            </SafeArea>
          </div>
        )}
        {hasHeader && (
          <div className={styles.header}>
            <SafeArea bottom={false}>
              <ActionsBar
                actions={headerActions}
                stackedActions={headerStackedActions}
                spacing={`${headerActionsSpacing}px`}
                scrollable={headerScrollable}
              />
            </SafeArea>
          </div>
        )}
      </div>
    </div>
  );
}
